import { MAX_EVENT_AGE_MS, STORAGE_KEYS } from './constants';
import { OpenView, PendingLogin, PendingSignUp, QueuedEvent } from './types';

// localStorage can throw (private mode, quota), so every access is guarded
const read = <T>(key: string): T | null => {
  try {
    const value = window.localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : null;
  } catch {
    return null;
  }
};

const write = (key: string, value: unknown): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable: the event is lost, the dashboard keeps working
  }
};

const remove = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const keysWithPrefix = (prefix: string): string[] => {
  const keys: string[] = [];
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(prefix)) keys.push(key);
    }
  } catch {
    // ignore
  }
  return keys;
};

export const isStorageAvailable = (): boolean => {
  try {
    const key = 'activityLogger:test';
    window.localStorage.setItem(key, '1');
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

/* Event queue: one array under a single key, shared by all tabs */

const readQueue = (): QueuedEvent[] => {
  const queue = read<QueuedEvent[]>(STORAGE_KEYS.QUEUE);
  return Array.isArray(queue) ? queue : [];
};

const writeQueue = (queue: QueuedEvent[]): void => {
  if (queue.length) {
    write(STORAGE_KEYS.QUEUE, queue);
  } else {
    remove(STORAGE_KEYS.QUEUE);
  }
};

// Read, append and write happen in one synchronous step, so the window in which another tab
// could write the queue at the same moment (and overwrite this event) is about a millisecond.
// Returns the number of queued events.
export const enqueueEvent = (event: QueuedEvent): number => {
  const queue = [...readQueue(), event];
  writeQueue(queue);
  return queue.length;
};

// Returns the user's queued events, oldest first. Events too old for the API are dropped, since
// one of them would make the server reject the whole batch.
export const getQueuedEvents = (userId: string): QueuedEvent[] => {
  const now = Date.now();
  const queue = readQueue();
  const fresh = queue.filter(
    (event) => event?.occurredAt && now - Date.parse(event.occurredAt) <= MAX_EVENT_AGE_MS,
  );
  if (fresh.length !== queue.length) writeQueue(fresh);

  // eventId is a UUIDv7, so it breaks ties between events with the same occurredAt
  return fresh
    .filter((event) => event.userId === userId)
    .sort(
      (a, b) =>
        Date.parse(a.occurredAt) - Date.parse(b.occurredAt) || a.eventId.localeCompare(b.eventId),
    );
};

// Re-reads the queue before removing, so events added while a request was in flight are kept
export const removeQueuedEvents = (eventIds: string[]): void => {
  const sentIds = new Set(eventIds);
  writeQueue(readQueue().filter((event) => !sentIds.has(event.eventId)));
};

/* Open view state, one key per tab */

const openViewKey = (tabId: string) => `${STORAGE_KEYS.OPEN_VIEW_PREFIX}${tabId}`;

export const saveOpenView = (tabId: string, view: OpenView): void => {
  write(openViewKey(tabId), { ...view, savedAt: Date.now() });
};

export const getOpenView = (tabId: string): OpenView | null => read<OpenView>(openViewKey(tabId));

export const removeOpenView = (tabId: string): void => remove(openViewKey(tabId));

// Open views left behind by other tabs, keyed by their tab id
export const getOtherOpenViews = (tabId: string): { tabId: string; view: OpenView }[] =>
  keysWithPrefix(STORAGE_KEYS.OPEN_VIEW_PREFIX)
    .filter((key) => key !== openViewKey(tabId))
    .map((key) => ({
      tabId: key.slice(STORAGE_KEYS.OPEN_VIEW_PREFIX.length),
      view: read<OpenView>(key),
    }))
    .filter((entry): entry is { tabId: string; view: OpenView } => Boolean(entry.view));

/* Sign-ups waiting for the first login, kept apart from the queue (they have no user yet) */

export const getPendingSignUps = (): PendingSignUp[] => {
  const signUps = read<PendingSignUp[]>(STORAGE_KEYS.PENDING_SIGN_UPS);
  return Array.isArray(signUps) ? signUps : [];
};

export const addPendingSignUp = (signUp: PendingSignUp): void =>
  write(STORAGE_KEYS.PENDING_SIGN_UPS, [...getPendingSignUps(), signUp]);

export const removePendingSignUps = (eventIds: string[]): void => {
  const sentIds = new Set(eventIds);
  const remaining = getPendingSignUps().filter((signUp) => !sentIds.has(signUp.event.eventId));

  if (remaining.length) {
    write(STORAGE_KEYS.PENDING_SIGN_UPS, remaining);
  } else {
    remove(STORAGE_KEYS.PENDING_SIGN_UPS);
  }
};

/* Login recorded on the auth callback, emitted once the user id is known */

export const savePendingLogin = (login: PendingLogin): void =>
  write(STORAGE_KEYS.PENDING_LOGIN, login);

export const takePendingLogin = (): PendingLogin | null => {
  const login = read<PendingLogin>(STORAGE_KEYS.PENDING_LOGIN);
  remove(STORAGE_KEYS.PENDING_LOGIN);
  return login;
};
